import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  useGetAvailableRewardsQuery,
  useGetRecoveryConfigQuery,
} from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ApprovedRewardsBody from 'scenes/RecoveryAI/components/ApprovedRewardsBody'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useMakeGoodCuration } from 'scenes/RecoveryAI/components/useMakeGoodCuration'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { useSaveAndTest } from './useSaveAndTest'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

const t = buildTranslate('recovery_ai.settings.approved_rewards')

// Horizontal gap between the two footer buttons (mirrors BrandVoicePane).
const SAVE_FOOTER_GAP = '16px'

// Order-independent equality for selection id arrays — both are duplicate-free
// (built only via toggle/remove), so length + every-includes is a correct
// set-equality for the dirty check.
const isSameSelection = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every(id => b.includes(id))

type Props = {
  // Still passed by Settings/index.tsx; unused since AI-1170 removed
  // Save-and-test. Drop with the parent in a follow-up.
  isActivated: boolean
}

// Settings → Approved rewards pane (AI-30, re-platformed for R12 / AI-168). The
// config-backed sibling of the wizard's ApprovedRewardsCard. R12 replaces the
// AI-30 debounced auto-save-on-select (and its in-flight failed-save rollback)
// with the explicit staged Save model (shared useSaveAndTest, same as Brand
// voice / Recovery generosity / Escalation rules) — a modal Cancel now owns
// rollback. The picker itself is the chrome-free ApprovedRewardsBody; this page
// wrapper keeps the load/error chrome, active-only filtering, and the Save
// footer.
// AI-1170: Save-and-test is removed everywhere — testing happens on the
// separate Agent Tuning page instead.
const ApprovedRewardsPane = (_props: Props) => {
  const [css] = useStyletron()
  // AI-64: admin-only writes. A view-only operator (RBAC can-view, not
  // view-and-edit) can reach this route, so every edit affordance goes disabled
  // — the ladder's Regenerate + decline "×", the picker trigger, each selected
  // reward's remove "×", and both persist buttons. Mirrors the CASL gate
  // EscalationRulesPane / DailyBudgetPane already apply.
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')

  // `isError` (the GET) surfaces a failed config load so we never present an
  // empty selection as if it were saved.
  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useGetRecoveryConfigQuery()
  // Rewards can be deactivated from the separate /rewards surface, which never
  // invalidates this cache — force a refetch on mount so a since-deactivated
  // reward is never offered as active. `isRewardsError` surfaces a failed
  // rewards GET instead of masquerading as "no rewards".
  const {
    data: availableRewards,
    isLoading: isRewardsLoading,
    isError: isRewardsError,
  } = useGetAvailableRewardsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  // Make-good ladder curation (AI-330/AI-383), unconditional. Its query is
  // deliberately independent of the config/rewards pair above: it is fail-soft
  // and its isLoading/isError are NEVER folded into the pane loading/error gates
  // below — a make-good 404 must degrade to an empty ladder, never blank the
  // working picker (CONTRACT §3). Shared with the Agent-Tuning modal host so the
  // two curation surfaces cannot drift.
  const makeGood = useMakeGoodCuration()
  // Pulled out so handleChange can depend on the callback itself rather than on
  // the whole curation object.
  const { clearFeedback: clearMakeGoodFeedback } = makeGood

  // Only active rewards are selectable: an inactive reward can't be issued, so
  // it must never be an option, count toward selection, or render as a chip.
  const activeRewards = useMemo(
    () => (availableRewards ?? []).filter(r => r.active),
    [availableRewards]
  )
  const activeRewardIds = useMemo(
    () => new Set(activeRewards.map(r => r.id)),
    [activeRewards]
  )

  // Local echo of the merchant's selection; seeded once from the saved config.
  // The PUT fires only on the explicit Save / Save-and-test buttons.
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const hasHydratedRef = useRef(false)
  useEffect(() => {
    if (!config || hasHydratedRef.current) return
    setSelectedIds(config.approved_reward_ids ?? [])
    hasHydratedRef.current = true
  }, [config])

  // Persist only ACTIVE ids (defense-in-depth, mirrors useWizardSubmit): a
  // since-deactivated reward hydrated from config must never be re-persisted.
  const buildPayload = useCallback(
    (): Partial<RecoveryConfig> => ({
      approved_reward_ids: selectedIds.filter(id => activeRewardIds.has(id)),
    }),
    [selectedIds, activeRewardIds]
  )

  // Dirty when the active-filtered selection differs from the saved (also
  // active-filtered) config set, so Save can't fire a no-op PUT and a deactivated
  // hydrated id doesn't read as a pending change.
  const isDirty = useMemo(() => {
    if (!config) return false
    const current = selectedIds.filter(id => activeRewardIds.has(id))
    const saved = (config.approved_reward_ids ?? []).filter(id =>
      activeRewardIds.has(id)
    )
    return !isSameSelection(current, saved)
  }, [config, selectedIds, activeRewardIds])

  // Save / Save-and-test orchestration (mutation, success flag, error/loading
  // flags, dirty-gating + canSave, post-save navigation + unsaved-guard bypass)
  // shared with the other RecoveryAI Settings panes.
  const {
    handleSave,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    canSave,
    guardBypassRef,
  } = useSaveAndTest(buildPayload, isDirty)

  // guardBypassRef lets Save-and-test's own post-save navigation through (AI-174).
  const unsavedChangesPrompt = useUnsavedChangesPrompt(isDirty, guardBypassRef)

  // Toggling/removing a reward only updates local state (+ drops a stale "Saved"
  // banner); the PUT fires on Save / Save-and-test.
  const handleChange = useCallback(
    (next: string[]) => {
      setSelectedIds(next)
      clearSuccess()
      // The pane has no modal-close event (unlike AgentTuningConfigModals), so a
      // stale regenerate error would otherwise persist with no attempt behind it.
      // Editing the picker is the "moved on" signal — clear it (AI-859).
      clearMakeGoodFeedback()
    },
    [clearSuccess, clearMakeGoodFeedback]
  )

  if (isConfigLoading || isRewardsLoading) {
    // The shared Spinner renders no role/aria text, so wrap it in a live status
    // region with an i18n accessible name (WCAG 4.1.2 Name, Role, Value).
    return (
      <div role="status" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // A failed CONFIG or REWARDS fetch withholds the picker and surfaces an error
  // — never the empty state — so a network failure isn't sold as "no rewards"
  // and an existing saved selection isn't masked as empty.
  if (isConfigError || isRewardsError || !config) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="alert">
        <Text.BodyText3 color="cayenne50">{t('fetch_error')}</Text.BodyText3>
      </div>
    )
  }

  return (
    <SettingsPaneChrome title={t('title')} subtitle={t('description')}>
      {/* Guard whole-route navigation (in-app + refresh/close) while dirty. */}
      {unsavedChangesPrompt}

      {/* Save failure — inline, non-blocking; tracks the mutation's isError. */}
      {isSaveError && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {t('save_error')}
          </Notification>
        </div>
      )}

      {/* Save success — POLITE status region (does not interrupt). */}
      {saveSuccess && !isSaveError && (
        <div className="mb-m">
          <Notification
            kind="positive"
            size="full"
            overrides={successNotificationOverrides}
          >
            {t('save_success')}
          </Notification>
        </div>
      )}

      <ApprovedRewardsBody
        rewards={activeRewards}
        value={selectedIds}
        onChange={handleChange}
        makeGoodOffers={makeGood.offers}
        onDeclineMakeGood={makeGood.onDecline}
        isDecliningMakeGood={makeGood.isDeclining}
        onRegenerateMakeGood={makeGood.onRegenerate}
        isRegeneratingMakeGood={makeGood.isRegenerating}
        makeGoodOffersLoaded={makeGood.offersLoaded}
        makeGoodRegenerateErrorKind={makeGood.regenerateErrorKind}
        makeGoodDeclineError={makeGood.declineError}
        disabled={!canManage}
      />

      <div className={`d-flex mt-xl ${css({ gap: SAVE_FOOTER_GAP })}`}>
        {/* AI-174: Save is REALLY disabled when there's nothing to persist; it
            disables in flight. AI-64: also disables outright for a view-only
            operator. AI-1170: Save-and-test is removed everywhere — testing
            happens on the separate Agent Tuning page instead. */}
        <Button
          kind="primary"
          onClick={handleSave}
          disabled={!canManage || !canSave}
          isLoading={isSaving}
        >
          {t('save_changes')}
        </Button>
      </div>
    </SettingsPaneChrome>
  )
}

export default ApprovedRewardsPane
